import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { ReceiptPreviewByApiDialog } from "@/modules/billing/presentation/ui/ReceiptPreviewByApiDialog";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  PlusIcon,
  PrinterIcon,
  ScrollTextIcon,
} from "lucide-react";
import { useState } from "react";
import { useRoomBookingQuery, useAssistInstructorsQuery } from "../api/queries";
import { RoomBookingReceiptPreviewDialog } from "../ui/RoomBookingReceiptPreviewDialog";

export function RoomBookingSuccessPage() {
  const nav = useNavigate({ from: "/app/room-bookings/success" });
  const search = useSearch({ from: "/app/room-bookings/success" });
  const bookingId = search.bookingId ?? "";

  const { data: booking, isLoading } = useRoomBookingQuery(bookingId);
  const { data: assistInstructorsResponse } =
    useAssistInstructorsQuery(bookingId);
  const assistInstructorNames =
    assistInstructorsResponse?.data?.map((a) => a.instructorName) ?? [];
  const [isReceiptDialogOpen, setIsReceiptDialogOpen] = useState(false);

  const handlePrintReceipt = () => {
    setIsReceiptDialogOpen(true);
  };

  const handleGoToDetail = () => {
    nav({
      to: "/app/room-bookings/$id",
      params: { id: bookingId },
    });
  };

  const handleBookAnother = () => {
    nav({ to: "/app/room-bookings/new" });
  };

  if (!bookingId) {
    nav({ to: "/app/room-bookings" });
    return null;
  }

  const roomBookingSuccessBreadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ການຈອງຫ້ອງ", to: "/app/room-bookings" },
        { label: "ສຳເລັດ" },
      ]}
    />
  );

  if (isLoading) {
    return (
      <>
        <Header>{roomBookingSuccessBreadcrumb}</Header>
        <Main>
          <div className="flex items-center justify-center py-16">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (!booking) {
    nav({ to: "/app/room-bookings" });
    return null;
  }

  const isCredit = search.settlementMode === "credit" && search.creditInvoiceId;
  const creditPaidNum =
    search.creditPaid != null && search.creditPaid !== ""
      ? Number.parseInt(search.creditPaid, 10)
      : null;
  const creditRemainingNum =
    search.creditRemaining != null && search.creditRemaining !== ""
      ? Number.parseInt(search.creditRemaining, 10)
      : null;
  const hasCreditSummary =
    isCredit &&
    creditPaidNum != null &&
    !Number.isNaN(creditPaidNum) &&
    creditRemainingNum != null &&
    !Number.isNaN(creditRemainingNum);

  if (booking.amount == null || booking.amount === 0) {
    nav({
      to: "/app/room-bookings/$id",
      params: { id: bookingId },
    });
    return null;
  }

  return (
    <>
      <Header>{roomBookingSuccessBreadcrumb}</Header>

      <Main>
        <div className="mx-auto max-w-2xl">
          <div className="rounded-xl border bg-card p-8 shadow-lg">
            <div className="mb-6 flex flex-col items-center">
              <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <CheckCircleIcon className="size-8 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="mb-2 font-bold text-2xl">
                {isCredit
                  ? "ຈອງຫ້ອງແລ້ວ — ໃບເກັບເງິນ"
                  : "ຈອງຫ້ອງແລະ ຊຳລະເງິນສຳເລັດ"}
              </h2>
              <p className="text-center text-muted-foreground text-sm">
                {isCredit
                  ? "ການຈອງຖືກບັນທຶກ · ຊຳລະຕໍ່ໄດ້ທີ່ໜ້າໃບເກັບເງິນ"
                  : "ການຈອງຫ້ອງຖືກບັນທຶກ ແລະ ຊຳລະເງິນແລ້ວ"}
              </p>
            </div>

            <div className="mb-6 space-y-6 border-t pt-6">
              <h3 className="font-semibold text-lg uppercase">ສະຫຼຸບການຈອງ</h3>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ຫ້ອງ
                  </p>
                  <p className="font-semibold">{booking.roomName}</p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ວັນທີ
                  </p>
                  <p className="font-semibold">{formatDate(booking.date)}</p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ເວລາ
                  </p>
                  <p className="font-semibold">
                    {formatTime(booking.startTime)} -{" "}
                    {formatTime(booking.endTime)}
                  </p>
                </div>
                <div>
                  <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                    ຊື່ / ວັດຖຸປະສົງ
                  </p>
                  <p className="font-semibold">
                    {booking.title || booking.contactName || "—"}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="mb-2 font-medium text-muted-foreground text-xs uppercase">
                  {isCredit ? "ຍອດໃບເກັບເງິນ" : "ຈຳນວນເງິນທີ່ຊຳລະ"}
                </p>
                {hasCreditSummary ? (
                  <div className="space-y-2">
                    <p className="font-bold text-2xl text-red-600">
                      {(booking.amount ?? 0).toLocaleString("lo-LA")} ກີບ
                    </p>
                    <p className="text-muted-foreground text-sm">
                      ຮັບແລ້ວ {creditPaidNum?.toLocaleString("lo-LA")} ₭ · ຄ້າງ{" "}
                      {creditRemainingNum?.toLocaleString("lo-LA")} ₭
                    </p>
                  </div>
                ) : (
                  <p className="font-bold text-3xl text-red-600">
                    {(booking.amount ?? 0).toLocaleString("lo-LA")} ກີບ
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3 border-t pt-6">
              {isCredit && search.creditInvoiceId ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    nav({
                      to: "/app/billing/credit-invoices/$id",
                      params: { id: search.creditInvoiceId ?? "" },
                    })
                  }
                >
                  <ScrollTextIcon className="mr-2 size-4" />
                  ໄປໜ້າໃບເກັບເງິນ
                </Button>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={handlePrintReceipt}
                  >
                    <PrinterIcon className="mr-2 size-4" />
                    ພິມໃບເກັບເງິນ
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={handleGoToDetail}
                  >
                    ໄປທີ່ລາຍລະອຽດການຈອງ
                    <ArrowRightIcon className="ml-2 size-4" />
                  </Button>
                </div>
              )}

              {isCredit && (
                <>
                  <Button
                    type="button"
                    className="w-full bg-red-600 hover:bg-red-700"
                    onClick={handleGoToDetail}
                  >
                    ໄປທີ່ລາຍລະອຽດການຈອງ
                    <ArrowRightIcon className="ml-2 size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={handleBookAnother}
                  >
                    <PlusIcon className="size-4" />
                    ຈອງຫ້ອງອີກ
                  </Button>
                </>
              )}

              {!isCredit && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={handleBookAnother}
                >
                  <PlusIcon className="size-4" />
                  ຈອງຫ້ອງອີກ
                </Button>
              )}
            </div>
          </div>
        </div>

        {!isCredit && search.receiptId ? (
          <ReceiptPreviewByApiDialog
            open={isReceiptDialogOpen}
            onOpenChange={setIsReceiptDialogOpen}
            receiptId={search.receiptId || null}
          />
        ) : !isCredit && booking ? (
          <RoomBookingReceiptPreviewDialog
            open={isReceiptDialogOpen}
            onOpenChange={setIsReceiptDialogOpen}
            roomName={booking.roomName}
            date={booking.date}
            startTime={booking.startTime}
            endTime={booking.endTime}
            title={booking.title}
            contactName={booking.contactName}
            amount={booking.amount ?? 0}
            bookingId={booking.id}
            assistInstructorNames={assistInstructorNames}
          />
        ) : null}
      </Main>
    </>
  );
}
